import {lazy, Suspense, JSX} from 'react'
import {Routes, Route, Navigate, useParams} from 'react-router'
import {userSelectors} from '../store/user/userSelectors'
import {userApi} from '../api/userApi'
import {getAuthCredential} from '../utils'
import {Auth} from '../screens/Auth'
import {BaseLayout} from '../layout/BaseLayout/BaseLayout'
import {MainLayout} from '../layout/MainLayout/MainLayout'
import {IdeationSetup} from '../screens/IdeationSettingsExplore/components/IdeationSettingsExploreWrapper/IdeationSetup/IdeationSetup'
import {IdeationResults} from '../screens/IdeationSettingsExplore/components/IdeationSettingsExploreWrapper/IdeationResults/IdeationResults'
import {NotfoundPage} from '../screens/NotFoundPage'
import {LoadingPage} from '../screens/LoadingPage'
import {IdeationInsights} from '../screens/IdeationSettingsExplore/components/IdeationSettingsExploreWrapper/IdeationInsights/IdeationInsights'
import {Dialog} from '../screens/Dialog/Dialog'
import {Dialogs} from '../screens/Dialogs/Dialogs'
import {UserDialog} from '../screens/UserDialog/UserDialog'
import {conversationsPath} from '../constants/pathConstants'
import {BaseFooter} from '../components/Footer/BaseFooter'
import {routePreload} from './routePreload'

// Sidebar destinations share their import thunk with the hover/idle prefetch in
// MainLayout (see routePreload.ts) so a warmed chunk never suspends here.
const Home = lazy(() => routePreload.home().then(m => ({default: m.Home})))
const Initiatives = lazy(() =>
  routePreload.initiatives().then(m => ({default: m.Initiatives})),
)
const TeamView = lazy(() =>
  routePreload.teamView().then(m => ({default: m.TeamView})),
)
const Analytics = lazy(() =>
  routePreload.analytics().then(m => ({default: m.Analytics})),
)
const Admin = lazy(() => routePreload.admin().then(m => ({default: m.Admin})))
const Settings = lazy(() =>
  routePreload.settings().then(m => ({default: m.Settings})),
)
const Initiative = lazy(() =>
  import('../screens/Initiative/Initiative').then(m => ({
    default: m.Initiative,
  })),
)
const CreateOrEditInitiative = lazy(() =>
  import('../screens/CreateOrEditInitiative/CreateOrEditInitiative').then(
    m => ({default: m.CreateOrEditInitiative}),
  ),
)
const Ideation = lazy(() =>
  import('../screens/Ideation/Ideation').then(m => ({default: m.Ideation})),
)
const IdeationSettingsExplore = lazy(() =>
  import('../screens/IdeationSettingsExplore/IdeationSettingsExplore').then(
    m => ({default: m.IdeationSettingsExplore}),
  ),
)
const Template = lazy(() =>
  import('../screens/Template/Template').then(m => ({default: m.Template})),
)
// const Workforce = lazy(() => import("../screens/Workforce"));

/**
 * Legacy per-tab initiative URLs (`/tasks`, `/huddles`, `/ideation`) collapsed into
 * the single initiative screen: in the v4 design Notes and Ideas are an in-page rail,
 * not sibling routes. Keeps bookmarks and e-mailed links working.
 *
 * Built from `useParams` rather than a relative `<Navigate to="../" />`: on a flat
 * (non-nested) route, react-router resolves `..` against the whole route path, which
 * would land on `/` instead of the initiative.
 */
const InitiativeTabRedirect = (): JSX.Element => {
  const {initID} = useParams<{initID: string}>()
  return <Navigate to={`/initiatives/${initID ?? ''}`} replace />
}

export const Routers = (): JSX.Element => {
  const token = getAuthCredential()
  const currentUser = userSelectors.useCurrentUser()
  const selectedWorkforceName = userSelectors.useSelectedWorkforceName()
  const {isLoading: workforcesLoading} = userApi.useWorkforces(token)

  return (
    <Routes>
      <Route
        path="/empower/ideation/:ideationId"
        element={
          <Suspense fallback={<LoadingPage />}>
            <Ideation status={''} />
          </Suspense>
        }
      />
      <Route path="/authorization/callback" element={<Auth />} />
      <Route
        path="/empower/draft/:ideationId"
        element={
          <Suspense fallback={<LoadingPage />}>
            <Ideation status={'draft'} />
          </Suspense>
        }
      />
      <Route
        path="public/conversations/:dialogId"
        element={
          <Suspense fallback={<LoadingPage />}>
            <UserDialog />
          </Suspense>
        }
      />

      <Route path="/" element={<BaseLayout />}>
        {selectedWorkforceName ? (
          <>
            {/*
              Pathless layout route: MainLayout (the sidebar) mounts once above
              these screens instead of once per route element. The sidebar keeps
              its open/closed state across navigations, and its active-tab
              highlight — derived from useLocation() — updates on the same commit
              as the URL even while the screen chunk is still loading inside
              MainLayout's own Suspense boundary.
            */}
            <Route element={<MainLayout />}>
              {['/', '/home'].map(path => (
                <Route key={path} path={path} element={<Home />} />
              ))}
              <Route path="/initiatives" element={<Initiatives />} />
              <Route path="/initiatives/:initID" element={<Initiative />} />
              <Route
                path="/initiatives/:initID/:tab"
                element={<InitiativeTabRedirect />}
              />
              <Route path="/team-view" element={<TeamView />} />
              <Route path={conversationsPath} element={<Dialogs />} />
              <Route path="/conversations/:dialogId" element={<Dialog />} />
              <Route path="/analytics" element={<Analytics />} />
              {currentUser?.isAdmin && (
                <Route path="/admin" element={<Admin />} />
              )}
              <Route path="/settings" element={<Settings />} />
            </Route>

            {/* No sidebar on these — they keep their own Suspense boundary. */}
            {['new-initiative', 'update-initiative'].map(path => (
              <Route
                path={path}
                key={path}
                element={
                  <Suspense fallback={<LoadingPage />}>
                    <CreateOrEditInitiative />
                    <BaseFooter />
                  </Suspense>
                }
              />
            ))}

            <Route
              path="/ideation"
              element={
                <Suspense fallback={<LoadingPage />}>
                  <IdeationSettingsExplore />
                  <BaseFooter />
                </Suspense>
              }
            >
              <Route path="insights" element={<IdeationInsights />} />
              <Route path="setup" element={<IdeationSetup />} />
              <Route path="results" element={<IdeationResults />} />
            </Route>

            {['new-template', 'update-template'].map(path => (
              <Route
                path={path}
                key={path}
                element={
                  <Suspense fallback={<LoadingPage />}>
                    <Template />
                  </Suspense>
                }
              />
            ))}
          </>
        ) : workforcesLoading ? (
          <Route path="*" element={<LoadingPage />} />
        ) : (
          <Route path="*" element={<Navigate to="/home" />} />
        )}

        <Route path="*" element={<NotfoundPage />} />
      </Route>
    </Routes>
  )
}
